/**
 * SmartLink A/B rotation — operator UI.
 *
 * Sections:
 *   1. SmartLink ID input          — drives list / save / assign.
 *   2. Variant editor table        — add / remove rows; inline edit of
 *                                    variant_key, offer_id, split_bps.
 *   3. Live sum indicator          — must == 10_000 bps before Save.
 *   4. Assignment tester           — POST /assign with a click_id and
 *                                    surface the picked variant.
 *   5. Current variant list        — server-side truth after the last
 *                                    successful read/save.
 *
 * Brand: emerald->sky gradient headline (text-brand-gradient), zinc
 * borders, 6-8px rounded, light theme only, no emojis, no competitor
 * names. Uses TanStack Query for fetch/mutation, sonner for toasts.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	assignClick,
	type AssignResponse,
	listVariants,
	MAX_VARIANTS,
	saveVariants,
	smartlinkAbQueryKeys,
	sumBps,
	TOTAL_BPS,
	validateClickId,
	type VariantDraft,
	type VariantRow,
	variantsFormSchema,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — keep classnames grouped for readable JSX.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const CELL_INPUT =
	"w-full rounded-[6px] border border-zinc-200 bg-white px-2 py-1.5 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";

const DEFAULT_DRAFT: VariantDraft = {
	variant_key: "",
	offer_id: "",
	split_bps: 0,
};

function mkEvenSplit(count: number): VariantDraft[] {
	// Produce a roster with an even split that always sums to TOTAL_BPS.
	if (count <= 0) return [];
	const base = Math.floor(TOTAL_BPS / count);
	const remainder = TOTAL_BPS - base * count;
	const rows: VariantDraft[] = [];
	for (let i = 0; i < count; i += 1) {
		rows.push({
			variant_key: `v${String.fromCharCode(65 + i)}`,
			offer_id: "",
			split_bps: base + (i < remainder ? 1 : 0),
		});
	}
	return rows;
}

export interface SmartlinkAbPageProps {
	/** When provided, prefills and locks the SmartLink ID field. */
	initialSmartlinkId?: string;
}

export function SmartlinkAbPage({ initialSmartlinkId }: SmartlinkAbPageProps = {}) {
	const qc = useQueryClient();

	// ---------------- SmartLink ID state ----------------
	const [smartlinkId, setSmartlinkId] = useState<string>(initialSmartlinkId ?? "");
	const trimmed = smartlinkId.trim();
	const canQuery = trimmed.length > 0;

	// ---------------- Editor rows ----------------
	const [rows, setRows] = useState<VariantDraft[]>(() => mkEvenSplit(2));
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [hydratedFor, setHydratedFor] = useState<string | null>(null);

	// ---------------- Assignment tester ----------------
	const [clickId, setClickId] = useState<string>("");
	const [clickIdError, setClickIdError] = useState<string | null>(null);
	const [lastAssign, setLastAssign] = useState<AssignResponse | null>(null);

	// ---------------- Load existing variants ----------------
	const query = useQuery({
		queryKey: smartlinkAbQueryKeys.bySmartlink(trimmed),
		queryFn: () => listVariants(trimmed),
		enabled: canQuery,
	});

	useEffect(() => {
		if (!canQuery) {
			setHydratedFor(null);
			return;
		}
		if (query.isSuccess && hydratedFor !== trimmed) {
			const data = query.data ?? [];
			if (data.length > 0) {
				setRows(
					data.map((r) => ({
						variant_key: r.variant_key,
						offer_id: r.offer_id,
						split_bps: r.split_bps,
					})),
				);
			} else {
				setRows(mkEvenSplit(2));
			}
			setErrors({});
			setHydratedFor(trimmed);
		}
	}, [query.isSuccess, query.data, trimmed, hydratedFor, canQuery]);

	// ---------------- Mutations ----------------
	const saveMutation = useMutation({
		mutationFn: saveVariants,
		onSuccess: (result, args) => {
			toast.success("Variants saved.");
			qc.setQueryData(smartlinkAbQueryKeys.bySmartlink(args.smartlinkId), result);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to save variants.");
		},
	});

	const assignMutation = useMutation({
		mutationFn: assignClick,
		onSuccess: (result) => {
			setLastAssign(result);
			toast.success(`Assigned to ${result.variant_key}.`);
		},
		onError: (err: Error) => {
			setLastAssign(null);
			toast.error(err.message || "Unable to assign click.");
		},
	});

	// ---------------- Derived state ----------------
	const total = useMemo(() => sumBps(rows), [rows]);
	const totalValid = total === TOTAL_BPS;
	const sumDelta = total - TOTAL_BPS;

	const savedVariants: VariantRow[] = useMemo(
		() => (query.data ?? []) as VariantRow[],
		[query.data],
	);

	const busy = saveMutation.isPending || assignMutation.isPending;

	// ---------------- Row handlers ----------------
	function addRow() {
		if (rows.length >= MAX_VARIANTS) {
			toast.error(`Maximum ${MAX_VARIANTS} variants.`);
			return;
		}
		setRows((prev) => [...prev, { ...DEFAULT_DRAFT }]);
	}

	function removeRow(index: number) {
		setRows((prev) => prev.filter((_r, i) => i !== index));
		// Clear index-specific errors so stale ones don't linger on the new row at that idx.
		setErrors((prev) => {
			const next: Record<string, string> = {};
			for (const [k, v] of Object.entries(prev)) {
				if (!k.startsWith(`row_${index}_`)) next[k] = v;
			}
			return next;
		});
	}

	function updateRow(index: number, field: keyof VariantDraft, value: string) {
		setRows((prev) =>
			prev.map((r, i) => {
				if (i !== index) return r;
				if (field === "split_bps") {
					const n = value === "" ? 0 : Number(value);
					return { ...r, split_bps: Number.isFinite(n) ? n : 0 };
				}
				return { ...r, [field]: value } as VariantDraft;
			}),
		);
		setErrors((prev) => {
			const key = `row_${index}_${field}`;
			if (!(key in prev) && !prev._global) return prev;
			const next = { ...prev };
			delete next[key];
			if (field === "split_bps") delete next._global;
			return next;
		});
	}

	function handleEvenSplit() {
		if (rows.length === 0) return;
		setRows((prev) => {
			const count = prev.length;
			const base = Math.floor(TOTAL_BPS / count);
			const remainder = TOTAL_BPS - base * count;
			return prev.map((r, i) => ({
				...r,
				split_bps: base + (i < remainder ? 1 : 0),
			}));
		});
		setErrors((prev) => {
			const { _global: _drop, ...rest } = prev;
			return rest;
		});
	}

	function handleSave(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = variantsFormSchema.safeParse({ smartlinkId, rows });
		if (!parsed.success) {
			setErrors(parsed.errors);
			toast.error("Please fix the highlighted rows.");
			return;
		}
		setErrors({});
		saveMutation.mutate({
			smartlinkId: parsed.data.smartlinkId,
			rows: parsed.data.rows,
		});
	}

	function handleAssign(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!canQuery) {
			toast.error("Enter a SmartLink ID first.");
			return;
		}
		const checked = validateClickId(clickId);
		if (!checked.ok) {
			setClickIdError(checked.error);
			return;
		}
		setClickIdError(null);
		assignMutation.mutate({ smartlinkId: trimmed, clickId: checked.value });
	}

	// ---------------- Render ----------------
	return (
		<div className="flex flex-col gap-8">
			<SmartlinkIdField
				value={smartlinkId}
				onChange={(v) => {
					setSmartlinkId(v);
					setHydratedFor(null);
					setLastAssign(null);
				}}
				error={errors.smartlinkId}
				loading={query.isFetching}
				locked={Boolean(initialSmartlinkId)}
			/>

			<form
				onSubmit={handleSave}
				className={`${CARD} flex flex-col gap-5 p-6`}
				noValidate
			>
				<EditorHeader
					count={rows.length}
					onAdd={addRow}
					onEvenSplit={handleEvenSplit}
					busy={busy}
				/>

				<VariantTable
					rows={rows}
					errors={errors}
					onRemove={removeRow}
					onChange={updateRow}
					disabled={saveMutation.isPending}
				/>

				<SumIndicator
					total={total}
					delta={sumDelta}
					valid={totalValid}
					globalError={errors._global}
				/>

				<div className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
					<button
						type="submit"
						disabled={saveMutation.isPending || !totalValid}
						className="rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{saveMutation.isPending ? "Saving." : "Save variants"}
					</button>
				</div>
			</form>

			<AssignmentTester
				clickId={clickId}
				onClickIdChange={(v) => {
					setClickId(v);
					if (clickIdError) setClickIdError(null);
				}}
				onSubmit={handleAssign}
				busy={assignMutation.isPending}
				error={clickIdError}
				last={lastAssign}
				disabled={!canQuery}
			/>

			<SavedVariantList variants={savedVariants} loading={query.isLoading} hasSmartlink={canQuery} />
		</div>
	);
}

// ---------------------------------------------------------------------------
// Sub-components — colocated for a single-file feature surface.
// ---------------------------------------------------------------------------

function SmartlinkIdField(props: {
	value: string;
	onChange: (v: string) => void;
	error?: string;
	loading?: boolean;
	locked?: boolean;
}) {
	return (
		<div className={`${CARD} flex flex-col gap-1.5 p-6`}>
			<label htmlFor="smartlink-ab-id" className={LABEL}>
				SmartLink ID
			</label>
			<input
				id="smartlink-ab-id"
				name="smartlinkId"
				type="text"
				autoComplete="off"
				placeholder="e.g. sl_alpha_01"
				className={INPUT}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				disabled={props.locked}
				aria-invalid={Boolean(props.error)}
				aria-describedby={props.error ? "smartlink-ab-id-error" : undefined}
			/>
			<div className="flex items-center justify-between">
				<p className={HINT}>
					Identifier of the SmartLink whose A/B rotation you want to edit.
				</p>
				{props.loading && (
					<span className={HINT} aria-live="polite">
						Loading existing variants.
					</span>
				)}
			</div>
			{props.error && (
				<p id="smartlink-ab-id-error" className={ERROR_TEXT}>
					{props.error}
				</p>
			)}
		</div>
	);
}

function EditorHeader(props: {
	count: number;
	onAdd: () => void;
	onEvenSplit: () => void;
	busy: boolean;
}) {
	return (
		<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
			<div className="flex flex-col gap-1">
				<h2 className="text-base font-semibold text-zinc-900">
					Variant editor
				</h2>
				<p className={HINT}>
					{props.count} variant{props.count === 1 ? "" : "s"}. Split must total
					exactly {TOTAL_BPS} bps.
				</p>
			</div>
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={props.onEvenSplit}
					disabled={props.busy || props.count === 0}
					className="rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Even split
				</button>
				<button
					type="button"
					onClick={props.onAdd}
					disabled={props.busy || props.count >= MAX_VARIANTS}
					className="rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Add variant
				</button>
			</div>
		</div>
	);
}

function VariantTable(props: {
	rows: VariantDraft[];
	errors: Record<string, string>;
	onRemove: (i: number) => void;
	onChange: (i: number, field: keyof VariantDraft, value: string) => void;
	disabled?: boolean;
}) {
	if (props.rows.length === 0) {
		return (
			<div className="rounded-[6px] border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center text-sm text-zinc-600">
				No variants yet. Use "Add variant" to create the first row.
			</div>
		);
	}
	return (
		<div className="overflow-hidden rounded-[6px] border border-zinc-200">
			<table className="w-full border-collapse text-sm">
				<thead className="bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
					<tr>
						<th className="px-3 py-2" scope="col">
							Variant key
						</th>
						<th className="px-3 py-2" scope="col">
							Offer ID
						</th>
						<th className="px-3 py-2" scope="col">
							Split bps
						</th>
						<th className="px-3 py-2" scope="col">
							<span className="sr-only">Actions</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{props.rows.map((row, idx) => {
						const keyErr = props.errors[`row_${idx}_variant_key`];
						const offerErr = props.errors[`row_${idx}_offer_id`];
						const bpsErr = props.errors[`row_${idx}_split_bps`];
						return (
							<tr
								key={`row-${idx}`}
								className="border-t border-zinc-200 align-top"
							>
								<td className="px-3 py-2">
									<input
										type="text"
										aria-label={`Variant key for row ${idx + 1}`}
										className={CELL_INPUT}
										value={row.variant_key}
										onChange={(e) =>
											props.onChange(idx, "variant_key", e.currentTarget.value)
										}
										disabled={props.disabled}
										aria-invalid={Boolean(keyErr)}
									/>
									{keyErr && <p className={`${ERROR_TEXT} mt-1`}>{keyErr}</p>}
								</td>
								<td className="px-3 py-2">
									<input
										type="text"
										aria-label={`Offer ID for row ${idx + 1}`}
										className={CELL_INPUT}
										value={row.offer_id}
										onChange={(e) =>
											props.onChange(idx, "offer_id", e.currentTarget.value)
										}
										disabled={props.disabled}
										aria-invalid={Boolean(offerErr)}
									/>
									{offerErr && (
										<p className={`${ERROR_TEXT} mt-1`}>{offerErr}</p>
									)}
								</td>
								<td className="px-3 py-2">
									<input
										type="number"
										min={0}
										max={TOTAL_BPS}
										step={1}
										inputMode="numeric"
										aria-label={`Split bps for row ${idx + 1}`}
										className={`${CELL_INPUT} max-w-[8rem]`}
										value={Number.isFinite(row.split_bps) ? row.split_bps : 0}
										onChange={(e) =>
											props.onChange(idx, "split_bps", e.currentTarget.value)
										}
										disabled={props.disabled}
										aria-invalid={Boolean(bpsErr)}
									/>
									{bpsErr && <p className={`${ERROR_TEXT} mt-1`}>{bpsErr}</p>}
								</td>
								<td className="px-3 py-2 text-right">
									<button
										type="button"
										aria-label={`Remove row ${idx + 1}`}
										onClick={() => props.onRemove(idx)}
										disabled={props.disabled}
										className="rounded-[6px] border border-zinc-300 bg-white px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
									>
										Remove
									</button>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function SumIndicator(props: {
	total: number;
	delta: number;
	valid: boolean;
	globalError?: string;
}) {
	const pct = Math.min(100, Math.max(0, (props.total / TOTAL_BPS) * 100));
	const fillClass = props.valid ? "bg-emerald-500" : "bg-amber-500";
	return (
		<div
			className={`flex flex-col gap-2 rounded-[6px] border ${
				props.valid ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
			} p-3`}
			aria-live="polite"
		>
			<div className="flex items-center justify-between text-sm">
				<span className="font-medium text-zinc-800">
					Total split: <span aria-label="Current sum of split bps">{props.total}</span>{" "}
					/ {TOTAL_BPS} bps
				</span>
				<span
					className={`text-xs font-medium ${
						props.valid ? "text-emerald-700" : "text-amber-700"
					}`}
				>
					{props.valid
						? "Balanced"
						: props.delta > 0
							? `Over by ${props.delta}`
							: `Under by ${Math.abs(props.delta)}`}
				</span>
			</div>
			<div
				className="h-1.5 overflow-hidden rounded-full bg-zinc-200"
				role="progressbar"
				aria-valuemin={0}
				aria-valuemax={TOTAL_BPS}
				aria-valuenow={props.total}
			>
				<div
					className={`h-full ${fillClass} transition-[width] duration-150 ease-out`}
					style={{ width: `${pct}%` }}
				/>
			</div>
			{props.globalError && (
				<p className={ERROR_TEXT} role="alert">
					{props.globalError}
				</p>
			)}
		</div>
	);
}

function AssignmentTester(props: {
	clickId: string;
	onClickIdChange: (v: string) => void;
	onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
	busy: boolean;
	error: string | null;
	last: AssignResponse | null;
	disabled: boolean;
}) {
	return (
		<form
			onSubmit={props.onSubmit}
			className={`${CARD} flex flex-col gap-4 p-6`}
			noValidate
		>
			<div className="flex flex-col gap-1">
				<h2 className="text-base font-semibold text-zinc-900">
					Assignment tester
				</h2>
				<p className={HINT}>
					Send a click ID through the deterministic bucketer and see which
					variant it lands on.
				</p>
			</div>
			<div className="flex flex-col gap-1.5">
				<label htmlFor="smartlink-ab-click-id" className={LABEL}>
					Click ID
				</label>
				<div className="flex flex-col gap-2 sm:flex-row">
					<input
						id="smartlink-ab-click-id"
						name="clickId"
						type="text"
						autoComplete="off"
						placeholder="e.g. clk_test_001"
						className={INPUT}
						value={props.clickId}
						onChange={(e) => props.onClickIdChange(e.currentTarget.value)}
						aria-invalid={Boolean(props.error)}
					/>
					<button
						type="submit"
						disabled={props.busy || props.disabled}
						className="rounded-[6px] border border-sky-600 bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-sky-700 hover:border-sky-700 disabled:cursor-not-allowed disabled:opacity-60 sm:whitespace-nowrap"
					>
						{props.busy ? "Assigning." : "Test assignment"}
					</button>
				</div>
				{props.error && <p className={ERROR_TEXT}>{props.error}</p>}
			</div>
			{props.last && <AssignResult result={props.last} />}
		</form>
	);
}

function AssignResult(props: { result: AssignResponse }) {
	return (
		<dl className="grid grid-cols-1 gap-3 rounded-[6px] border border-sky-200 bg-sky-50 p-3 text-sm sm:grid-cols-3">
			<div>
				<dt className={HINT}>Variant key</dt>
				<dd className="font-medium text-zinc-900">{props.result.variant_key}</dd>
			</div>
			<div>
				<dt className={HINT}>Offer ID</dt>
				<dd className="font-medium text-zinc-900">{props.result.offer_id}</dd>
			</div>
			<div>
				<dt className={HINT}>Recorded at</dt>
				<dd className="font-medium text-zinc-900">
					{new Date(props.result.ts * 1000).toLocaleTimeString()}
				</dd>
			</div>
		</dl>
	);
}

function SavedVariantList(props: {
	variants: VariantRow[];
	loading: boolean;
	hasSmartlink: boolean;
}) {
	if (!props.hasSmartlink) return null;
	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<div className="flex flex-col gap-1">
				<h2 className="text-base font-semibold text-zinc-900">
					Current variants
				</h2>
				<p className={HINT}>
					Server-side truth for this SmartLink, sorted by variant key.
				</p>
			</div>
			{props.loading ? (
				<p className={HINT}>Loading.</p>
			) : props.variants.length === 0 ? (
				<p className={HINT}>No variants saved yet.</p>
			) : (
				<ul className="flex flex-col divide-y divide-zinc-200 rounded-[6px] border border-zinc-200">
					{props.variants.map((v) => (
						<li
							key={v.id}
							className="flex items-center justify-between px-3 py-2 text-sm"
						>
							<span className="font-medium text-zinc-900">{v.variant_key}</span>
							<span className="text-zinc-600">{v.offer_id}</span>
							<span className="tabular-nums text-zinc-700">
								{v.split_bps} bps
							</span>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

export default SmartlinkAbPage;
